/** Shared center/feet placement and foreground-depth compensation. Keep gameplay positions aligned with rendered sprites; test production math directly. */

import { TILE } from '../engine/constants'

/**
 * Where a visual hangs from its anchor.
 *
 * Two conventions, and holding it to two is the point:
 *
 *   - `centre`: the anchor already is the centre. Entities, because a 14x14
 *     hitbox sits inside a 16x16 sprite and the art is centred on the box.
 *   - `foot`: the anchor is the bottom edge, so the centre is half a sprite
 *     above it. Objects and the player, which stand on something.
 */
export type Anchor = 'centre' | 'foot'

/**
 * Convert an anchor to the vertical centre of the sprite.
 *
 * Both a plane and a modelled mesh are centred on their own origin, so this
 * conversion is what every placement needs. The sprite's rendered height is
 * `spriteH`; the result is the world Y its centre should sit at.
 */
export function anchorToCentre(
  anchorY: number,
  spriteH: number,
  anchor: Anchor,
): number {
  return anchor === 'centre' ? anchorY : anchorY - spriteH / 2
}

/**
 * The footprint bottom an object at `ty` with height `hTiles` stands on.
 *
 * `tx` is deliberately absent: vertical anchoring has nothing to do with the
 * column, and leaving it out makes that explicit. An earlier version took `tx`
 * and ignored it, which is a small lie in a helper's signature.
 */
export function objectFoot(ty: number, hTiles: number, oy = 0): number {
  return (ty + hTiles) * TILE + oy
}

/** Place foreground art on the same screen coordinates as its collision plane. */
export function foregroundPlacement(
  x: number,
  y: number,
  z: number,
  camera: { x: number; y: number; z: number },
): { x: number; y: number; scale: number } {
  const scale = (camera.z - z) / camera.z
  return {
    x: camera.x + (x - camera.x) * scale,
    y: camera.y + (y - camera.y) * scale,
    scale,
  }
}
